import React, { useState, useEffect } from "react";
import API from "../../api";
import toastr from "toastr";

interface Department {
  id?: number;
  dept_name: string;
  description?: string;
  status?: number | string;
}

interface Props {
  show: boolean;
  onClose: () => void;
  mode: "add" | "edit";
  deptData: Department | null;
  onSuccess: () => void;
}

const modalOverlay: React.CSSProperties = {
  position: "fixed", inset: 0,
  background: "rgba(0,0,0,0.65)", zIndex: 1040,
};
const modalWrapper: React.CSSProperties = {
  position: "fixed", inset: 0, zIndex: 1050,
  display: "flex", alignItems: "center", justifyContent: "center",
  padding: "16px", overflowY: "auto",
};
const modalBox: React.CSSProperties = {
  width: "100%", maxWidth: "500px",
  background: "#1a1f2e", borderRadius: "10px",
  border: "1px solid #2d3748",
  boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
};
const modalHeader: React.CSSProperties = {
  padding: "14px 20px", borderBottom: "1px solid #2d3748",
  background: "#111827", borderRadius: "10px 10px 0 0",
  display: "flex", alignItems: "center", justifyContent: "space-between",
};
const modalFooter: React.CSSProperties = {
  padding: "12px 20px", borderTop: "1px solid #2d3748",
  background: "#111827", borderRadius: "0 0 10px 10px",
  display: "flex", justifyContent: "flex-end", gap: "10px",
};
const inputCls = "form-control form-control-sm bg-dark text-white border-secondary";
const labelStyle: React.CSSProperties = { color: "#d1d5db", fontSize: "13px" };

const DepartmentModal: React.FC<Props> = ({
  show, onClose, mode, deptData, onSuccess,
}) => {
  const [deptName, setDeptName]       = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus]           = useState<number>(1);
  const [loading, setLoading]         = useState(false);

  useEffect(() => {
    if (!show) return;
    if (mode === "edit" && deptData) {
      setDeptName(deptData.dept_name || "");
      setDescription(deptData.description || "");
      setStatus(deptData.status !== undefined ? Number(deptData.status) : 1);
    } else {
      setDeptName("");
      setDescription("");
      setStatus(1); // always Active on add
    }
  }, [show, mode, deptData]);

  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!deptName.trim()) {
      toastr.error("Department name is required");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        dept_name: deptName.trim(),
        description: description.trim() || null,
        status: String(status),
      };

      if (mode === "add") {
        await API.post("departments/createDepartment", payload);
        toastr.success("Department created successfully");
      } else {
        await API.put(`departments/updateDepartment/${deptData?.id}`, payload);
        toastr.success("Department updated successfully");
      }
      onSuccess();
    } catch (err: any) {
      toastr.error(err.response?.data?.message || "Operation failed");
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <>
      <div style={modalOverlay} onClick={handleClose} />
      <div style={modalWrapper}>
        <div style={modalBox} onClick={(e) => e.stopPropagation()}>

          {/* Header */}
          <div style={modalHeader}>
            <h5 className="mb-0 text-white fw-semibold" style={{ fontSize: "15px" }}>
              {mode === "add" ? (
                <><i className="fa fa-plus-circle me-2 text-danger" />Create New Department</>
              ) : (
                <><i className="fa fa-edit me-2 text-warning" />Edit Department</>
              )}
            </h5>
            <button
              type="button" className="btn-close btn-close-white"
              onClick={handleClose} style={{ opacity: 0.6 }}
            />
          </div>

          {/* Body */}
          <form onSubmit={handleSubmit}>
            <div style={{ padding: "20px" }}>

              {/* Department Name */}
              <div className="mb-3">
                <label className="form-label" style={labelStyle}>
                  Department Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={inputCls}
                  value={deptName}
                  onChange={(e) => setDeptName(e.target.value)}
                  placeholder="Enter department name"
                  required
                />
              </div>

              {/* Description */}
              <div className="mb-3">
                <label className="form-label" style={labelStyle}>Description</label>
                <textarea
                  className={inputCls}
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Enter description (optional)"
                  style={{ resize: "vertical" }}
                />
              </div>

              {/* Status — only shown in edit mode */}
              {mode === "edit" && (
                <div className="mb-1">
                  <label className="form-label" style={labelStyle}>Status</label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(Number(e.target.value))}
                    className={inputCls}
                  >
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </select>
                </div>
              )}

            </div>

            {/* Footer */}
            <div style={modalFooter}>
              <button
                type="button"
                className="btn btn-sm btn-secondary px-4"
                onClick={handleClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={`btn btn-sm px-4 ${mode === "add" ? "btn-danger" : "btn-warning"}`}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" role="status" />
                    Saving...
                  </>
                ) : (
                  <><i className="fa fa-save me-1" />{mode === "add" ? "Save" : "Update"}</>
                )}
              </button>
            </div>
          </form>

        </div>
      </div>
    </>
  );
};

export default DepartmentModal;